/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  BuilderNavigationService,
  CommonActions,
  CommonFlowState,
  FormStateBuilder,
  ListActionPipelines,
  UsageDialogConfig,
  usageDialogRoute,
} from '@gh/builder-shared';
import {
  BuilderPermissionSet,
  BulkActionEntityType,
  BulkUsageResponse,
  ComponentGridFilter,
  UsageEntityType,
  UsageRestService,
  Usages,
} from '@gh/builder-shared-data';
import {
  AppRouter,
  filterableForm,
  FilterableFormState,
  getRouteInitData,
  UserContext,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { filterTracker, RouteDataService, SlicedDataSource } from '@gh/core-data';
import {
  DialogService,
  NotificationService,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { cursorTracker, newGridPaginationCursor } from '@gh/core-ui-grid';
import { CostCenterListItem } from '../../resources/cost-center-list-item.model';
import { EstimatingCostCenterListTabBusinessService } from '../../resources/estimating-cost-center-list-tab-business.service';
import { CostCenterGridDefinition } from './cost-center-grid-definition.service';
import { filter$ } from '@gh/rx/operators';
import { DEFAULT_COMPONENT_FILTER } from '../../resources/component-list-item.model';

@Component({
  standalone: false,
  selector: 'gh-estimating-cost-center-list-tab',
  templateUrl: 'estimating-cost-center-list-tab.component.html',
  styleUrls: ['estimating-cost-center-list-tab.component.scss'],
  providers: [CostCenterGridDefinition],
})
export class EstimatingCostCenterListTabComponent implements OnInit {
  dataSource: SlicedDataSource<CostCenterListItem>;
  cursor = newGridPaginationCursor();
  form: FilterableFormState<ComponentGridFilter>;
  loadedData: CostCenterListItem[] = [];
  activeLoadedData: CostCenterListItem[] = [];
  checkList: any[] = [];
  get isShowDeleteSelected(): boolean {
    return !!this.checkList.length;
  }

  constructor(private route: ActivatedRoute,
              private userContext: UserContext,
              private routeData: RouteDataService,
              private businessService: EstimatingCostCenterListTabBusinessService,
              public gridDefinition: CostCenterGridDefinition,
              private router: AppRouter,
              private waitingService: WaitingService,
              private navigationService: BuilderNavigationService,
              private pipelines: ListActionPipelines,
              private dialogService: DialogService,
              private notifications: NotificationService,
              private usageService: UsageRestService,
              private formStateBuilder: FormStateBuilder,
              private commonActions: CommonActions) {

  }

  ngOnInit(): void {
    const { gridLayout } = getRouteInitData(this.route);
    this.gridDefinition.bind(gridLayout);

    this.form = filterableForm(this.formStateBuilder.buildPlain(
      {
        value: DEFAULT_COMPONENT_FILTER,
        children: {
          showDeleted: void 0,
          searchString: void 0,
        },
      }));
    const tracker = this.routeData.tracker(filterTracker(this.form)).withEffects(cursorTracker(this.cursor));
    tracker.listen(this.route);
    tracker.filterChanges.subscribe(() => this.loadData());
  }

  onSearch(): void {
    this.form.emitFilter();
  }

  onCreate(): void {
    this.router.navigateToChild(['/setup/estimating/cost-centers/new']);
  }

  onOpen(costCenter: CostCenterListItem): void {
    if (this.userContext.hasPermission(BuilderPermissionSet.Setup.Estimating.CostCenters.EDIT_VIEW)) {
      this.router.navigateToChild(['/setup/estimating/cost-centers', costCenter.id]);
    }
  }

  onViewUsage(costCenter: CostCenterListItem): void {
    this.dialogService.openWithInput(usageDialogRoute, <UsageDialogConfig>{
      entityId: costCenter.id,
      entityType: UsageEntityType.CC_USAGE_INFO,
    });
  }

  onActivate(costCenter: CostCenterListItem): void {
    const flow = this.pipelines.toggleItem({
      question: {
        id: 'msg_cc_undelete',
        params: {
          name: costCenter.name,
        },
      },
      execute: this.commonActions.execute((state: CommonFlowState<CostCenterListItem>) =>
        this.businessService.activate(state.value)),
      finish: () => this.loadData(),
    });

    flow.run({
      value: costCenter,
    });
  }

  onDeactivate(costCenter: CostCenterListItem): void {
    const flow = this.pipelines.checkUsageToggleItem({
      messages: {
        confirmUsed: {
          id: 'msg_cc_mark_as_deleted',
          params: {
            name: costCenter.name,
          },
        },
        confirmNotUsed: {
          id: 'msg_cc_delete',
          params: {
            name: costCenter.name,
          },
        },
      },
      executes: {
        checkUsage: this.commonActions.execute((state: CommonFlowState<any>) =>
          this.businessService.checkUsage(state.value)),
        summary: this.commonActions.execute((state: CommonFlowState<any>) =>
          this.businessService.deactivateOrDelete(costCenter, state.value)),
      },
      finish: () => this.loadData(),
    });

    flow.run({
      value: costCenter,
    });
  }

  goToAel(id?: ID): void {
    this.navigationService.goToAel({
      entityType: 'CC',
      entityId: id || void 0,
      label: 'lbl_cost_centers',
    });
  }

  onDeleteSelected(): void {
    const entityList = this.loadedData
      .filter((model) => this.checkList.includes(model.id))
      ?.map((item) => ({id: item.id, version: item.version}));

    this.usageService.getBulkUsageInfo(<any[]>entityList, BulkActionEntityType.costCenters)
      .pipe(this.waitingService.operator())
      .subscribe((res: BulkUsageResponse) => {
      this.dialogService.confirmation(
        yesNoConfirmation(res.allDeleted ? 'msg_cc_delete_selected' : 'msg_cc_delete_mark_as_deleted'),
      ).pipe(
        filter$(yesAnswer),
      ).subscribe(
        () => this.businessService.bulkDelete(res.usages.map((resItem: Usages) => ({
          id: resItem.itemId,
          usage: {
            version: resItem.version,
            deleted: resItem.deleted,
          },
        }))).subscribe(() => this.loadData(), this.notifications.processAll),
        this.notifications.processAll);
    },           this.notifications.processAll);
  }
  rowClicked(event: any): void {
    if (event.preventDefault) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (event.event.shiftKey) {
      this.onCheckChange(event.data.id);
      return;
    }
    if (this.isActionButtonsVisible()) {
      this.onOpen(event.data);
    }
  }

  onToggleSelectAll(): void {
    if (!this.checkList.length || (!!this.checkList.length && this.checkList.length < this.activeLoadedData.length)) {
      this.checkList = this.activeLoadedData?.map((item) => item.id);
      return;
    }
    this.checkList = [];
  }

  onCheckChange(id: ID): void {
    const targetItem = this.loadedData.find((item) => item.id === id);
    if (!targetItem || !this.canBeSelected(targetItem)) {
      return;
    }
    if (this.checkList.includes(id)) {
      this.checkList.splice(this.checkList.indexOf(id), 1);
      this.checkList = [...this.checkList];
      return;
    }
    this.checkList = [...this.checkList, id];
  }

  isActionButtonsVisible(): boolean {
    return !this.checkList.length;
  }

  onLoadData(event: any): void {
    this.checkList = [];
    this.loadedData = event.data;
    this.activeLoadedData = event.data.filter(this.canBeSelected);
  }

  canBeSelected(item: CostCenterListItem): boolean {
    return item.isActive;
  }

  private loadData(): void {
    this.dataSource = this.businessService.findAllAsDataSource(this.form.submitValue);
  }
}
